/**
 * Game Activity Service
 *
 * Navigating out to a game, and noticing when the user comes back.
 *
 * "Played today" is no longer tracked here: under D22 it's derived from the
 * `plays` rows the server holds, so clicking a game link writes a play row
 * (with a null score) and the dashboard reports it back. What's left is the
 * recent-click marker, which stays device-local by design.
 */

import gameActivityStorage from '../utils/gameActivityStorage';
import { debugLog, debugError } from '../utils/debugLogger';
import { trackGameLinkClick } from './analyticsService';

/**
 * Store recent game link click for auto-redirect functionality
 * @param {string} gameId The game ID
 * @param {string} gameUrl The game URL
 * @returns {boolean} True if successfully stored
 */
export const storeRecentGameClick = (gameId, gameUrl) => {
  const clickTime = Date.now();
  debugLog(`Storing game click: ${gameId} at ${new Date(clickTime).toLocaleTimeString()}`);
  const success = gameActivityStorage.setRecentGameClick(gameId, gameUrl, clickTime);

  if (success) {
    debugLog(`✓ Stored recent game click: ${gameId}`, 'success');
  } else {
    debugError(`✗ Failed to store game click: ${gameId}`);
  }

  return success;
};

/**
 * Check if there was a recent game visit (within last 10 minutes) that needs score entry
 * @returns {Object|null} Object with gameId and gameUrl if recent visit found, null otherwise
 */
export const checkForRecentGameVisit = () => {
  debugLog('Checking for recent game visit...');
  const recentClick = gameActivityStorage.getRecentGameClick();

  if (!recentClick) {
    debugLog('No recent click found in storage');
    return null;
  }

  const currentTime = Date.now();
  const tenMinutesInMs = 10 * 60 * 1000;
  // Long enough to cover the blur/focus cycle that can fire while the browser
  // is still leaving for the game - a prompt on the way out would interrupt
  // the click that caused it. Short enough that bouncing straight back off a
  // game that didn't load still gets offered the form.
  const settleMs = 5 * 1000;

  const timeSinceClick = currentTime - recentClick.clickTime;
  debugLog(`Recent click found: ${recentClick.gameId}, ${Math.floor(timeSinceClick / 1000)}s ago`);

  const visitedRecently = timeSinceClick < tenMinutesInMs && timeSinceClick > settleMs;
  if (visitedRecently) {
    debugLog(`✓ Recent visit valid for game ${recentClick.gameId}`, 'success');
    return {
      gameId: recentClick.gameId,
      gameUrl: recentClick.gameUrl,
      clickTime: recentClick.clickTime
    };
  }

  debugLog(`Recent visit too old or too recent (${Math.floor(timeSinceClick / 1000)}s)`, 'warn');
  return null;
};

/**
 * Clear recent game visit data (called after score is entered or user dismisses)
 * @returns {boolean} True if successfully cleared
 */
export const clearRecentGameVisit = () => {
  debugLog('Clearing recent game visit data');
  const success = gameActivityStorage.clearRecentGameClick();

  if (success) {
    debugLog('✓ Cleared recent game visit data', 'success');
  } else {
    debugError('✗ Failed to clear recent game visit data');
  }

  return success;
};

/**
 * Navigate to a game URL
 * @param {string} gameUrl The game URL to navigate to
 */
export const navigateToGame = (gameUrl) => {
  window.location.href = gameUrl;
};

/**
 * Handle game link click - record the play, store the recent click, navigate.
 *
 * The play row is what marks the game as played today (D22), so it's written
 * for every game rather than only "linked" ones. `nlgid` is still only passed
 * through to games that can post a score back.
 *
 * Navigation happens regardless of whether the write succeeded - failing to
 * record a play is no reason to stop someone playing.
 *
 * @param {Object} game The game object
 * @param {Function} createEmptyRecord Function to create the empty play record
 * @param {boolean} isFavorite Whether the game is in the user's shortlist, for analytics
 * @returns {Promise<boolean>} True if the recent-click marker was stored
 */
export const handleGameLinkClick = async (game, createEmptyRecord, isFavorite = false) => {
  const {id: gameId, url: gameUrl, isLinked} = game;

  // Track analytics
  trackGameLinkClick(game, isFavorite);

  // Store recent game click for auto-redirect functionality
  const storeSuccess = storeRecentGameClick(gameId, gameUrl);

  if (!createEmptyRecord) {
    navigateToGame(gameUrl);
    return storeSuccess;
  }

  try {
    const response = await createEmptyRecord(gameId);

    // Linked games take the play id so they can post a score back to us.
    if (isLinked && response && response.id) {
      navigateToGame(gameUrl + `?nlgid=${response.id}`);
    } else {
      navigateToGame(gameUrl);
    }
  } catch (error) {
    console.error('Error creating empty score record, navigating anyway:', error);
    navigateToGame(gameUrl);
  }

  return storeSuccess;
};
